/**
 * Individual element chart with expand/collapse functionality
 * Shows element value over time with reference range shading
 */
import { useState, useMemo, type Key } from 'react';
import { Paper, Group, Text, ActionIcon, Tooltip, Box } from '@mantine/core';
import { IconMaximize, IconMinimize } from '@tabler/icons-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
} from 'recharts';
import { format } from 'date-fns';
import type { ElementHistoryRow } from '@/types/sensor';
import { calculateNiceAxisTicks } from '@/utils/formatters';
import { CHART_REFERENCE_LINE_COLOR } from '@/utils/chartColors';
import { PROVIDER_LABELS } from '@/utils/icpProviderDetect';

interface ElementChartProps {
  data: ElementHistoryRow;
}

const COLLAPSED_HEIGHT = 120;
const EXPANDED_HEIGHT = 300;

const LINE_COLOR = 'var(--mantine-color-blue-6)';

// Distinct marker shapes per provider, used only when a series spans ≥2
// providers so points are distinguishable without relying on the tooltip.
// Shapes are assigned deterministically by the provider's order of first
// appearance in the series.
type DotShape = 'circle' | 'square' | 'triangle';
const DOT_SHAPES: DotShape[] = ['circle', 'square', 'triangle'];

function providerLabel(provider: string): string {
  return (PROVIDER_LABELS as Record<string, string>)[provider] ?? provider ?? 'Unknown';
}

interface ChartPoint {
  date: number;
  value: number | null;
  in_range: boolean | null;
  barcode: string;
  provider: string;
}

interface ProviderDotProps {
  cx?: number;
  cy?: number;
  payload?: ChartPoint;
  shapeByProvider: Record<string, DotShape>;
}

/**
 * Custom Recharts dot that draws a per-provider shape (circle/square/triangle)
 * when the series spans multiple providers.
 */
function ProviderDot({ cx, cy, payload, shapeByProvider }: ProviderDotProps) {
  if (cx === undefined || cy === undefined || !payload) {
    return null;
  }
  const shape = shapeByProvider[payload.provider] ?? 'circle';
  const r = 3.5;
  const fill = LINE_COLOR;

  if (shape === 'square') {
    return (
      <rect
        x={cx - r}
        y={cy - r}
        width={r * 2}
        height={r * 2}
        fill={fill}
        stroke={fill}
      />
    );
  }
  if (shape === 'triangle') {
    const points = `${cx},${cy - r - 1} ${cx - r - 1},${cy + r} ${cx + r + 1},${cy + r}`;
    return <polygon points={points} fill={fill} stroke={fill} />;
  }
  return <circle cx={cx} cy={cy} r={r} fill={fill} stroke={fill} />;
}

export function ElementChart({ data }: ElementChartProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Filter out null values and format for Recharts
  const chartData: ChartPoint[] = data.data
    .filter((d) => d.value !== null)
    .map((d) => ({
      date: new Date(d.test_date).getTime(),
      value: d.value,
      in_range: d.in_range,
      barcode: d.barcode,
      provider: d.provider,
    }));

  const hasRange = data.range_min !== null && data.range_max !== null;

  // Calculate Y axis with nice ticks
  const yAxisConfig = useMemo(() => {
    const values = chartData.map((d) => d.value as number);
    let minValue = Math.min(...values);
    let maxValue = Math.max(...values);

    // Extend to include reference range if present
    if (hasRange) {
      minValue = Math.min(minValue, data.range_min!);
      maxValue = Math.max(maxValue, data.range_max!);
    }

    // Use fewer ticks in collapsed mode
    const tickCount = isExpanded ? 5 : 4;

    return calculateNiceAxisTicks(minValue, maxValue, tickCount, true);
  }, [chartData, hasRange, data.range_min, data.range_max, isExpanded]);

  // Distinct providers in this series (in order of first appearance) and the
  // shape assigned to each. Multi-provider series get distinct point shapes.
  const { shapeByProvider, multiProvider } = useMemo(() => {
    const order: string[] = [];
    for (const p of chartData) {
      if (!order.includes(p.provider)) order.push(p.provider);
    }
    const mapping: Record<string, DotShape> = {};
    order.forEach((provider, idx) => {
      mapping[provider] = DOT_SHAPES[idx % DOT_SHAPES.length] ?? 'circle';
    });
    return { shapeByProvider: mapping, multiProvider: order.length >= 2 };
  }, [chartData]);

  if (chartData.length < 2) {
    return (
      <Paper p="sm" withBorder>
        <Group justify="space-between">
          <Group gap="xs">
            <Text fw={500}>{data.label}</Text>
            <Text size="xs" c="dimmed">{data.unit}</Text>
          </Group>
        </Group>
        <Text c="dimmed" ta="center" py="md" size="sm">
          Not enough data points
        </Text>
      </Paper>
    );
  }

  const height = isExpanded ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT;

  // When multiple providers are present, draw per-provider shapes; otherwise
  // keep the simple circle dot for the single-provider case. Recharts passes a
  // React `key` plus geometry/payload to the dot render function.
  const dotRenderer = multiProvider
    ? (props: {
        cx?: number;
        cy?: number;
        payload?: ChartPoint;
        key?: Key | null;
      }) => {
        const { key, cx, cy, payload } = props;
        return (
          <ProviderDot
            key={key ?? undefined}
            cx={cx}
            cy={cy}
            payload={payload}
            shapeByProvider={shapeByProvider}
          />
        );
      }
    : { r: 3, fill: LINE_COLOR };

  return (
    <Paper p="sm" withBorder>
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <Text fw={500}>{data.label}</Text>
          <Text size="xs" c="dimmed">{data.unit}</Text>
        </Group>
        <Tooltip label={isExpanded ? 'Collapse chart' : 'Expand chart'}>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Collapse chart' : 'Expand chart'}
          >
            {isExpanded ? <IconMinimize size={18} /> : <IconMaximize size={18} />}
          </ActionIcon>
        </Tooltip>
      </Group>

      <Box
        data-testid="chart-container"
        role="img"
        aria-label={`${data.label} chart showing ${chartData.length} data points${hasRange ? `, target range ${data.range_min} to ${data.range_max} ${data.unit}` : ''}`}
        style={{
          height: `${height}px`,
          transition: 'height 200ms ease-in-out',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
            {isExpanded && <CartesianGrid strokeDasharray="3 3" opacity={0.3} />}

            {/* Zero baseline - show when zero is in domain */}
            {yAxisConfig.domain[0] <= 0 && yAxisConfig.domain[1] >= 0 && (
              <ReferenceLine y={0} stroke={CHART_REFERENCE_LINE_COLOR} strokeWidth={1} />
            )}

            {/* Reference range shaded band */}
            {hasRange && (
              <ReferenceArea
                y1={data.range_min!}
                y2={data.range_max!}
                fill="var(--mantine-color-green-1)"
                fillOpacity={0.5}
                stroke="var(--mantine-color-green-4)"
                strokeOpacity={0.5}
                strokeDasharray="3 3"
              />
            )}

            <XAxis
              dataKey="date"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(ts) => format(new Date(ts), isExpanded ? 'MMM d' : 'M/d')}
              tick={{ fontSize: isExpanded ? 12 : 10 }}
              hide={!isExpanded}
            />
            <YAxis
              domain={yAxisConfig.domain}
              ticks={yAxisConfig.ticks}
              tick={{ fontSize: isExpanded ? 12 : 10 }}
              width={isExpanded ? 50 : 40}
              tickFormatter={yAxisConfig.tickFormatter}
            />

            <RechartsTooltip
              labelFormatter={(ts) => format(new Date(ts), 'MMM d, yyyy')}
              formatter={(value: number, _name: string, item: { payload?: ChartPoint }) => {
                const provider = item?.payload?.provider;
                const valueLabel = `${value.toFixed(data.unit === 'ppb' ? 1 : 2)} ${data.unit}`;
                const label = provider
                  ? `${data.label} (${providerLabel(provider)})`
                  : data.label;
                return [valueLabel, label];
              }}
              contentStyle={{
                backgroundColor: 'var(--mantine-color-body)',
                borderColor: 'var(--mantine-color-default-border)',
              }}
            />

            <Line
              type="monotone"
              dataKey="value"
              stroke={LINE_COLOR}
              strokeWidth={2}
              dot={dotRenderer}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* Provider marker legend when a series spans multiple providers */}
      {multiProvider && (
        <Group gap="md" mt="xs" justify="center">
          {Object.entries(shapeByProvider).map(([provider, shape]) => (
            <Group key={provider} gap={6}>
              <Box
                component="svg"
                width={12}
                height={12}
                viewBox="0 0 12 12"
                aria-hidden
              >
                {shape === 'square' && (
                  <rect x={2} y={2} width={8} height={8} fill={LINE_COLOR} />
                )}
                {shape === 'triangle' && (
                  <polygon points="6,1 1,11 11,11" fill={LINE_COLOR} />
                )}
                {shape === 'circle' && (
                  <circle cx={6} cy={6} r={4} fill={LINE_COLOR} />
                )}
              </Box>
              <Text size="xs" c="dimmed">
                {providerLabel(provider)}
              </Text>
            </Group>
          ))}
        </Group>
      )}

      {/* Range legend when expanded */}
      {isExpanded && hasRange && (
        <Group gap="xs" mt="xs" justify="center">
          <Box
            style={{
              width: 16,
              height: 16,
              backgroundColor: 'var(--mantine-color-green-1)',
              border: '1px dashed var(--mantine-color-green-4)',
            }}
          />
          <Text size="xs" c="dimmed">
            Target range: {data.range_min} - {data.range_max} {data.unit}
          </Text>
        </Group>
      )}
    </Paper>
  );
}
